import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Drawer, Layout, Menu } from "antd";
import {
  AreaChartOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  PoweroffOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import getMenuItems_vat from "../components/Menu_item_vat";
import "./LeftSidebar.css";

const { Sider } = Layout;

export const SIDEBAR_WIDTH = 240;
export const SIDEBAR_COLLAPSED_WIDTH = 64;
export const TOPBAR_HEIGHT = 60;

// Menu data uses zmdi icon names; map them to Ant Design icons.
const ICONS = {
  folder: <AreaChartOutlined style={{ color: "#ffd166" }} />,
  layers: <AppstoreOutlined style={{ color: "#4cc9f0" }} />,
  chart: <BarChartOutlined style={{ color: "#06d6a0" }} />,
  "account-circle": <UserOutlined style={{ color: "#ff9f1c" }} />,
  settings: <SettingOutlined style={{ color: "#c77dff" }} />,
  power: <PoweroffOutlined style={{ color: "#ff6b6b" }} />,
};

const toAntItems = (items) =>
  items.map((item, index) => {
    const key = item.submenu ? `group-${index}` : item.path;
    const icon = ICONS[item.icon] || <AppstoreOutlined style={{ color: "#fff" }} />;
    if (item.submenu) {
      return {
        key,
        icon,
        label: item.name,
        children: item.submenu.map((sub) => ({
          key: sub.path,
          label: <Link to={sub.path}>{sub.name}</Link>,
        })),
      };
    }
    return { key, icon, label: <Link to={item.path}>{item.name}</Link> };
  });

export default function LeftSidebar({ usertype, isOpen, isMobile, onClose }) {
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState([]);

  const menuItems = useMemo(() => getMenuItems_vat(usertype), [usertype]);
  const antItems = useMemo(() => toAntItems(menuItems), [menuItems]);

  // Highlight the current route and expand the group that contains it.
  const { selectedKey, parentKey } = useMemo(() => {
    let selected = null;
    let parent = null;
    menuItems.forEach((item, index) => {
      if (item.submenu) {
        const match = item.submenu.find((s) => s.path === location.pathname);
        if (match) {
          selected = match.path;
          parent = `group-${index}`;
        }
      } else if (item.path === location.pathname) {
        selected = item.path;
      }
    });
    return { selectedKey: selected, parentKey: parent };
  }, [menuItems, location.pathname]);

  useEffect(() => {
    if (parentKey) setOpenKeys((keys) => (keys.includes(parentKey) ? keys : [parentKey]));
  }, [parentKey]);

  const menu = (
    <Menu
      mode="inline"
      theme="dark"
      items={antItems}
      selectedKeys={selectedKey ? [selectedKey] : []}
      // Collapsed Sider renders submenus as popups, so only control keys while expanded.
      openKeys={isMobile || isOpen ? openKeys : undefined}
      onOpenChange={setOpenKeys}
      inlineCollapsed={!isMobile && !isOpen}
      onClick={() => isMobile && onClose()}
      className="app-sidebar-menu"
    />
  );

  if (isMobile) {
    return (
      <Drawer
        placement="left"
        open={isOpen}
        onClose={onClose}
        closable={false}
        size={Math.min(SIDEBAR_WIDTH + 40, 300)}
        zIndex={2100}
        className="app-sidebar-drawer"
        styles={{ body: { padding: 0 }, header: { display: "none" } }}
      >
        {menu}
      </Drawer>
    );
  }

  return (
    <Sider
      className="app-sidebar"
      theme="dark"
      collapsible
      trigger={null}
      collapsed={!isOpen}
      width={SIDEBAR_WIDTH}
      collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
      style={{ top: TOPBAR_HEIGHT, height: `calc(100vh - ${TOPBAR_HEIGHT}px)` }}
    >
      {menu}
    </Sider>
  );
}
